// @flow
import React, {Fragment} from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import {
  IconButton,
  Grid,
  Hidden,
  Box,
  Typography,
  Button,
  FormControlLabel,
  Checkbox,
  Snackbar,
} from '@material-ui/core';
import {colors} from '../../../../assets/styles/colors';
import SnackBar from '../../../../elements/SnackBar/SnackBar';
import {Alert} from '@material-ui/lab';
import {
  ArrowBack,
  StarBorderRounded,
  SmsOutlined,
  LocalMallOutlined,
} from '@material-ui/icons';

import type {Store} from '../../../../entities/index';
import {useIsMinimalPlan} from '../../../store/utils';

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(2, 2),
  },
  description: {
    color: 'grey',
    margin: theme.spacing(1, 1),
    fontSize: '14px',
  },
  subtitle: {
    color: colors.grey900,
    margin: theme.spacing(2, 1),
  },
  boxText: {
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(2),
    },
  },
  gridIcon: {
    margin: 'auto',
    textAlign: 'center',
  },
  icon: {
    backgroundColor: colors.purple1,
    borderRadius: '15px',
    padding: '4px',
  },
  subRouteHeader: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(1, 0),
  },
  backButton: {
    margin: 'auto',
    padding: theme.spacing(1, 4),
  },
  paper: {
    padding: theme.spacing(1),
  },
  snackBarSuccess: {
    backgroundColor: '#E5F7F2',
    color: '#00B282',
  },
  snackBarError: {
    backgroundColor: '#FDECEA',
    color: '#A05536',
  },
  subRouteHeaderSmall: {
    display: 'flex',
    padding: '20px',
    alignItems: 'center',
    margin: 'auto',
    justifyContent: 'center',
    backgroundColor: '#F8F9FB',
  },
  subRouteTitle: {
    marginLeft: theme.spacing(2),
    fontSize: '1rem',
    fontWeight: 600,
  },
  linkButton: {
    fontWeight: 600,
    textDecoration: 'none',
  },
}));

type AdvantageItem = {
  description: string,
  icon: any,
};

type Props = {
  handleToggleEnabled: () => void,
  enabled: boolean,
  isSaving?: boolean,
  error?: string,
  shippingError?: boolean,
  updateSuccess?: boolean,
  store: Store,
};

export default function WhatsAppOption(props: Props) {
  const {
    handleToggleEnabled,
    error,
    shippingError,
    updateSuccess,
    enabled,
    isSaving,
  } = props;
  const classes = useStyles();

  const isMinimal = useIsMinimalPlan();
  const advantagesList: Array<AdvantageItem> = [
    {
      description: 'Recibe directamente los pedidos a tu WhatsApp',
      icon: <LocalMallOutlined color="primary" className={classes.icon} />,
    },
    {
      description:
        'Interactúa con tu cliente en el momentó más crítico: el pago',
      icon: <SmsOutlined color="primary" className={classes.icon} />,
    },
    {
      description: 'Ofrece servicio de atención personalizado',
      icon: <StarBorderRounded color="primary" className={classes.icon} />,
    },
  ];

  return (
    <Fragment>
      <Snackbar
        open={shippingError}
        autoHideDuration={4000}
        // onClose={() => setShippingError(false)}
      >
        <Alert severity="info">
          Para desactivar esta opción debes desactivar: Permitir envíos fuera de
          la zona delivery.{' '}
          <Link
            to="/configure/logistics/shipping/advanced"
            className={classes.linkButton}
          >
            Clic aquí.
          </Link>
        </Alert>
      </Snackbar>
      <div className={classes.subRouteHeader}>
        <Hidden xsDown>
          <IconButton
            variant="contained"
            size="small"
            component={Link}
            to="/configure/payment-methods"
          >
            <ArrowBack />
          </IconButton>
        </Hidden>
        <Typography
          component="h6"
          variant="h6"
          className={classes.subRouteTitle}
        >
          Pedir por WhatsApp
        </Typography>
      </div>
      <Grid
        container
        spacing={3}
        alignContent="flex-start"
        className={classes.paper}
      >
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <FormControlLabel
            classes={{label: classes.checkboxLabel}}
            control={
              <Checkbox
                checked={enabled}
                onChange={handleToggleEnabled}
                color="primary"
                disabled={isSaving || isMinimal}
              />
            }
            label="Activar el método de pago"
          />
        </Grid>
        {enabled && (
          <Fragment>
            <Grid item xs={12} sm={12} md={11} lg={8}>
              <div className={classes.subRouterBody}>
                <Typography component="div" className={classes.subtitle}>
                  <Box
                    fontWeight={500}
                    fontSize={14}
                    className={classes.boxText}
                  >
                    Recibe los pedidos directamente a tu WhatsApp y coordina el
                    pago por interno.
                  </Box>
                  <Box
                    fontWeight={500}
                    fontSize={14}
                    className={classes.boxText}
                  >
                    Haz que comprarte sea fácil, usando aplicaciones del día a
                    día como WhatsApp.
                  </Box>
                </Typography>

                <Typography className={classes.description}>
                  ¿Por qué es importante?
                </Typography>

                <Typography className={classes.description}>
                  Se recomienda habilitar esta opción para poder resolver
                  posibles dudas del cliente y concretar la compra. Es la
                  modalidad de compra que más ha crecido durante la pandemia.
                </Typography>
              </div>
            </Grid>
            <Grid item xs={12} sm={12} md={11} lg={8}>
              <Typography className={classes.header}>
                Beneficios de WhatsApp
              </Typography>
              {advantagesList.map((item: AdvantageItem, idx: number) => (
                <Grid container key={`advantageItem-${idx}`}>
                  <Grid item xs={1} className={classes.gridIcon}>
                    {item.icon}
                  </Grid>
                  <Grid item xs={11} sm={11} md={11} lg={11}>
                    <Typography className={classes.description}>
                      {item.description}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Fragment>
        )}

        <Hidden smUp>
          <div className={classes.subRouteHeaderSmall}>
            <Button
              size="large"
              color="primary"
              fullWidth
              component={Link}
              to="/configure/payment-methods"
              disabled={isSaving}
              startIcon={<ArrowBack />}
            >
              <Typography color="primary" className={classes.subRouteTitle}>
                Regresar
              </Typography>
            </Button>
          </div>
        </Hidden>
      </Grid>
      <SnackBar
        mode="error"
        className={classes.snackBarError}
        message={error ? 'Error al guardar los cambios' : undefined}
      />
      <SnackBar
        mode="success"
        className={classes.snackBarSuccess}
        message={
          updateSuccess ? 'Se guardaron los cambios exitosamente' : undefined
        }
      />
    </Fragment>
  );
}

WhatsAppOption.propTypes = {
  enabled: PropTypes.bool.isRequired,
  shippingError: PropTypes.bool,
  isSaving: PropTypes.bool,
  handleToggleEnabled: PropTypes.func.isRequired,
  error: PropTypes.string,
  updateSuccess: PropTypes.bool,
  store: PropTypes.object.isRequired,
};
